// General
import PropTypes from 'prop-types';
import React, { Component } from 'react';
// Redux Form
import { Field, formValueSelector, reduxForm } from 'redux-form';
// Redux
import { connect } from 'react-redux';
// Translation
import { FormattedMessage, injectIntl } from 'react-intl';
// Style
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import {
  Button
} from 'react-bootstrap';

// Internal Component
import { getLocationData } from '../../actions/getLocation';
import {
  formatAddressFromGeocodeResult,
  geocodeLatLng,
} from '../../helpers/googleAddressFormat';
import { googleMapLoader } from '../../helpers/googleMapLoader';
import PlaceGeoSuggest from '../Common/GeoSuggest/PlaceGeoSuggest';
import Loader from '../Loader';
import PlaceMap from '../PlaceMap';
import FooterButton from './FooterButton';
import SidePanel from './SidePanel';
import stepFourIllustration from '/public/SiteImages/step4.svg';

import bt from '../../components/commonStyle.css';
import s from './ListPlaceStep1.css';

// Locale
import messages from '../../locale/messages';
// Helpers
import update from './update';
import validate from './validate';

function getDistanceFromLatLngInMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Ámbito de búsqueda restringido a Bolivia.
// Se usa restricción nativa de Google (componentRestrictions + includedRegionCodes)
// en lugar de concatenar ",BO" al texto, que falla con nombres ambiguos o errores de tipeo.
const BOLIVIA_INCLUDED_REGION_CODES = ['bo'];
const BOLIVIA_REGION_CODE = 'bo';
const BOLIVIA_BOUNDS = { north: -9.68, south: -22.9, east: -57.45, west: -69.65 };
const BOLIVIA_LOCATION_BIAS = {
  rectangle: {
    low: { latitude: BOLIVIA_BOUNDS.south, longitude: BOLIVIA_BOUNDS.west },
    high: { latitude: BOLIVIA_BOUNDS.north, longitude: BOLIVIA_BOUNDS.east },
  },
};

function isGeocodeResultInBolivia(result) {
  const components = result?.address_components || [];
  return components.some(
    (component) =>
      (component.types || []).includes('country') &&
      (component.short_name === 'BO' || component.long_name === 'Bolivia')
  );
}

function getBoliviaLatLngBounds() {
  if (window.google?.maps?.LatLng && window.google?.maps?.LatLngBounds) {
    return new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(BOLIVIA_BOUNDS.south, BOLIVIA_BOUNDS.west),
      new window.google.maps.LatLng(BOLIVIA_BOUNDS.north, BOLIVIA_BOUNDS.east)
    );
  }
  return undefined;
}

const CITY_TYPES = ['locality', 'postal_town', 'administrative_area_level_3', 'administrative_area_level_2'];

function findComponentByTypes(components, types, longKey, shortKey) {
  return (components || []).find(component =>
    (component.types || []).some(type => types.includes(type))
  );
}

// Regla para texto tipeado a mano: se separa por comas.
// 3 partes (2 comas) -> street, city, country (state se copia de city para
// cumplir validate.js que exige state no vacío).
// 4+ partes (3+ comas) -> street, city, state (intermedios), country (último).
// Menos de 3 partes -> campos faltantes vacíos para que validate bloquee "Siguiente".
function splitTypedAddress(text) {
  const parts = (text || '').split(',').map(part => part.trim()).filter(Boolean);
  if (parts.length >= 4) {
    return {
      street: parts[0],
      city: parts[1],
      state: parts.slice(2, -1).join(', '),
      country: parts[parts.length - 1],
    };
  }
  if (parts.length === 3) {
    return { street: parts[0], city: parts[1], state: parts[1], country: parts[2] };
  }
  if (parts.length === 2) {
    return { street: parts[0], city: parts[1], state: '', country: '' };
  }
  if (parts.length === 1) {
    return { street: parts[0], city: '', state: '', country: '' };
  }
  return { street: '', city: '', state: '', country: '' };
}

// Parsea addressComponents de Places API (New): { types, longText, shortText }.
// Los huecos se rellenan con la regla de split por comas para que
// validate.js (street, city, state, country) siempre tenga valores.
function parsePlaceDetailAddress(data, fallbackText) {
  const components = data?.addressComponents || [];
  const getLong = (types) => findComponentByTypes(components, types)?.longText || '';
  const getShort = (types) => findComponentByTypes(components, types)?.shortText || '';
  const route = getLong(['route']);
  const streetNumber = getLong(['street_number']);
  const typed = splitTypedAddress(fallbackText);
  const street = [route, streetNumber].filter(Boolean).join(' ')
    || data?.displayName?.text
    || typed.street
    || fallbackText
    || '';
  return {
    street,
    city: getLong(CITY_TYPES) || typed.city,
    state: getLong(['administrative_area_level_1']) || getShort(['administrative_area_level_1']) || typed.state,
    country: getShort(['country']) || getLong(['country']) || typed.country || 'BO',
    zipcode: getLong(['postal_code']) || getShort(['postal_code']) || '',
  };
}

// Parsea address_components del Geocoder JS: { types, long_name, short_name }.
function parseGeocoderAddress(result, fallbackText) {
  const components = result?.address_components || [];
  const getLong = (types) => findComponentByTypes(components, types)?.long_name || '';
  const getShort = (types) => findComponentByTypes(components, types)?.short_name || '';
  const route = getLong(['route']);
  const streetNumber = getLong(['street_number']);
  const typed = splitTypedAddress(fallbackText);
  return {
    street: [route, streetNumber].filter(Boolean).join(' ') || typed.street,
    city: getLong(CITY_TYPES) || typed.city,
    state: getLong(['administrative_area_level_1']) || getShort(['administrative_area_level_1']) || typed.state,
    country: getShort(['country']) || getLong(['country']) || typed.country || 'BO',
    zipcode: getLong(['postal_code']) || getShort(['postal_code']) || '',
  };
}

class Page6 extends Component {

  static propTypes = {
    initialValues: PropTypes.object,
    previousPage: PropTypes.any,
    nextPage: PropTypes.any,
    locationMap: PropTypes.object,
    isMapTouched: PropTypes.bool,
    lat: PropTypes.number,
    lng: PropTypes.number,
    googleMapBrowserApiKey: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      initialLat: props.lat,
      initialLng: props.lng,
      searchAddress: '',
      addressValue: '',
      isSearching: false,
      isLocating: false,
      locationError: false,
    };
    this.handleSearch = this.handleSearch.bind(this);
    this.handleUseCurrentLocation = this.handleUseCurrentLocation.bind(this);
    this.useCurrentLocationAsInitial = this.useCurrentLocationAsInitial.bind(this);
    this.latestSearchId = 0;
    this._isMounted = false;
    this._hasAutoLocated = false;
  }

  renderPlaceMap = ({ input, label, meta: { touched, error }, lat, lng, isMapTouched, mapWarning, mapSuccess }) => {
    const { formatMessage } = this.props.intl;
    return (
      <div className={'stpeOneMap'}>
        {touched && error && <span>{formatMessage(error)}</span>}
        <PlaceMap
          {...input}
          lat={lat}
          lng={lng}
          isMapTouched={isMapTouched}
          mapWarning={mapWarning}
          mapSuccess={mapSuccess}
          onPinMoved={this.handlePinMoved}
        />
      </div>
    )
  }

  handlePinMoved = async ({ lat, lng }) => {
    const { googleMapBrowserApiKey } = this.props;
    const doGeocode = () => {
      geocodeLatLng(lat, lng)
        .then(result => {
          const address = formatAddressFromGeocodeResult(result);
          if (!address) return;
          const fields = parseGeocoderAddress(result, address);
          this.applyLocationFields({ lat, lng, fields, displayAddress: address });
        })
        .catch(error => console.error(error));
    };

    if (window.google?.maps?.Geocoder) {
      doGeocode();
    } else {
      googleMapLoader(googleMapBrowserApiKey).then(doGeocode);
    }
  }

  getCoordsFromPlaceDetail(data) {
    const lat = data?.location?.latitude;
    const lng = data?.location?.longitude;
    if (lat != null && lng != null) return { lat, lng };
    const geometryLocation = data?.geometry?.location;
    if (geometryLocation) {
      const latVal = typeof geometryLocation.lat === 'function' ? geometryLocation.lat() : geometryLocation.lat;
      const lngVal = typeof geometryLocation.lng === 'function' ? geometryLocation.lng() : geometryLocation.lng;
      if (latVal != null && lngVal != null) return { lat: latVal, lng: lngVal };
    }
    return null;
  }

  applyLocationFields({ lat, lng, fields, displayAddress }) {
    // Setea todo lo que validate.js exige (street, city, state, country) más
    // lat/lng/zipcode, sin pasar por el backend: el initialize de getLocationData
    // pisa con null los niveles que Google no devuelve y bloquea el "Siguiente".
    if (lat != null) this.props.autofill('lat', lat);
    if (lng != null) this.props.autofill('lng', lng);
    this.props.autofill('street', fields.street || '');
    this.props.autofill('city', fields.city || '');
    this.props.autofill('state', fields.state || '');
    this.props.autofill('country', fields.country || 'BO');
    this.props.autofill('zipcode', fields.zipcode || '');
    this.props.autofill('isMapTouched', true);
    // Pin movido / sugerencia elegida / ubicación actual: se setea la
    // dirección geocodificada en AMBOS inputs (buscador y dirección).
    if (displayAddress != null) this.setState({ searchAddress: displayAddress, addressValue: displayAddress });
  }

  handleSuggestSelect = (data, value) => {
    if (!value) return;
    const coords = this.getCoordsFromPlaceDetail(data);
    if (coords) {
      // El click en una sugerencia ya trae ubicación precisa (Place Details):
      // conservar el texto completo seleccionado y setear los campos desde
      // addressComponents, sin re-geocodificar ni llamar al backend.
      const fullAddress = data?.formattedAddress || value;
      const fields = parsePlaceDetailAddress(data, fullAddress);
      this.applyLocationFields({ lat: coords.lat, lng: coords.lng, fields, displayAddress: fullAddress });
      return;
    }
    if (value) {
      this.setState({ searchAddress: value }, () => {
        this.handleSearch();
      });
    }
  }

  handlePlaceGeoSuggestChange = (value) => {
    // Solo afecta al input del buscador: no toca el pin ni el input dirección.
    this.setState({ searchAddress: value || '' });
  }

  handleAddressInputChange = (e) => {
    // Input dirección manual: independiente del buscador y del pin.
    // No muestra sugerencias ni mueve el mapa; solo parsea por comas a
    // street/city/state/country para que update.js guarde ese valor.
    const value = e?.target?.value ?? '';
    this.setState({ addressValue: value });
    const fields = splitTypedAddress(value);
    this.props.autofill('street', fields.street || '');
    this.props.autofill('city', fields.city || '');
    this.props.autofill('state', fields.state || '');
    this.props.autofill('country', fields.country || '');
  }

  handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleSearch();
    }
  }

  handleSearch() {
    const { searchAddress } = this.state;
    const { googleMapBrowserApiKey } = this.props;

    if (!searchAddress.trim()) return;

    const searchId = ++this.latestSearchId;
    this.setState({ isSearching: true });

    const doGeocode = () => {
      const geocoder = new window.google.maps.Geocoder();
      const request = {
        address: searchAddress,
        componentRestrictions: { country: 'BO' },
        region: 'BO',
      };
      const bounds = getBoliviaLatLngBounds();
      if (bounds) request.bounds = bounds;
      geocoder.geocode(request, (results, status) => {
        if (searchId !== this.latestSearchId) return;
        const firstResultInBolivia = (results || []).find(isGeocodeResultInBolivia);
        if (status === 'OK' && firstResultInBolivia) {
          const { lat, lng } = firstResultInBolivia.geometry.location;
          const latVal = typeof lat === 'function' ? lat() : lat;
          const lngVal = typeof lng === 'function' ? lng() : lng;
          // Texto tipeado a mano: los campos salen de los componentes del
          // geocoder y los huecos se completan con la regla de split por comas.
          // Se conserva el texto tipeado en el input (no el formateado recortado).
          const fields = parseGeocoderAddress(firstResultInBolivia, searchAddress);

          this.applyLocationFields({ lat: latVal, lng: lngVal, fields, displayAddress: searchAddress });
        } else {
          console.error('Geocode error (solo Bolivia):', status, results);
        }
        this.setState({ isSearching: false });
      });
    };

    if (window.google?.maps?.Geocoder) {
      doGeocode();
    } else {
      googleMapLoader(googleMapBrowserApiKey).then(() => {
        doGeocode();
      }).catch(() => {
        this.setState({ isSearching: false });
      });
    }
  }

  componentDidMount() {
    this._isMounted = true;
    const { street, city, state, country, lat, lng } = this.props;
    let addressParts = [street, city, state, country]
      .map(part => (part || '').trim())
      .filter(Boolean);
    // Evita mostrar "ciudad, ciudad" cuando state se copió de city (formato 2 comas).
    if (addressParts.length === 4 && addressParts[1] === addressParts[2]) {
      addressParts = [addressParts[0], addressParts[1], addressParts[3]];
    }
    const existingAddress = addressParts.join(', ');
    if (existingAddress) {
      this.setState({ searchAddress: existingAddress, addressValue: existingAddress });
    }

    const hasCoords = lat != null && lng != null;
    const hasAddress = Boolean(existingAddress || street);
    // Si ya hay punto guardado, mantener comportamiento actual.
    // Si no hay dirección ni coordenadas, usar ubicación actual como datos iniciales.
    if (!hasCoords && !hasAddress) {
      this.useCurrentLocationAsInitial();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  useCurrentLocationAsInitial() {
    if (this._hasAutoLocated || !navigator.geolocation) return;
    this._hasAutoLocated = true;
    this.setState({ isLocating: true, locationError: false });

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!this._isMounted) return;
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        if (lat == null || lng == null) {
          this.setState({ isLocating: false });
          return;
        }
        this.applyCurrentPosition(lat, lng, () => {
          if (this._isMounted) this.setState({ isLocating: false });
        });
      },
      () => {
        if (this._isMounted) this.setState({ isLocating: false, locationError: true });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  applyCurrentPosition(lat, lng, done) {
    const { googleMapBrowserApiKey } = this.props;
    this.props.autofill('lat', lat);
    this.props.autofill('lng', lng);
    this.props.autofill('isMapTouched', true);

    const geocode = () => {
      geocodeLatLng(lat, lng)
        .then(result => {
          if (!this._isMounted) return;
          const formattedAddress = formatAddressFromGeocodeResult(result);
          if (!formattedAddress) return;
          const fields = parseGeocoderAddress(result, formattedAddress);
          this.applyLocationFields({ lat, lng, fields, displayAddress: formattedAddress });
        })
        .catch(error => console.error(error))
        .finally(() => done && done());
    };

    if (window.google?.maps?.Geocoder) {
      geocode();
    } else {
      googleMapLoader(googleMapBrowserApiKey).then(() => {
        geocode();
      }).catch(() => done && done());
    }
  }

  handleUseCurrentLocation(e) {
    if (e) e.preventDefault();
    if (!navigator.geolocation) return;
    this.setState({ isLocating: true });
    navigator.geolocation.getCurrentPosition((pos) => {
      if (!this._isMounted) return;
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      this.applyCurrentPosition(lat, lng, () => {
        if (this._isMounted) this.setState({ isLocating: false });
      });
    }, () => {
      if (this._isMounted) this.setState({ isLocating: false });
    });
  }

  render() {

    const { error, handleSubmit, submitting, pristine, previousPage, nextPage } = this.props;
    const { locationMap, isMapTouched, lat, lng, formPage, step } = this.props;
    const { street, city, state: stateName, country } = this.props;
    const { formatMessage } = this.props.intl;
    const { initialLat, initialLng, searchAddress, addressValue } = this.state;
    let isDisabled = true;
    // Habilitar "Siguiente" con lo mismo que exige validate.js + coordenadas para el pin.
    if (
      lat != null && lng != null &&
      (street || '').trim() && (city || '').trim() &&
      (stateName || '').trim() && (country || '').trim()
    ) {
      isDisabled = false;
    }

    const isFarFromAddress = lat && lng && initialLat && initialLng
      ? getDistanceFromLatLngInMeters(initialLat, initialLng, lat, lng) > 200
      : false;

    return (
      <div className={s.eslipinBg}>
        <div className={s.grid}>
          <SidePanel
            badge="Información del alojamiento"
            title={`¿Dónde se encuentra \n`}
            highlightText="tu espacio?"
            subtitle={`Indícanos la ubicación exacta para que \n los huéspedes puedan encontrarte \n fácilmente.`}
            iconImg={stepFourIllustration}
          />
          <form onSubmit={handleSubmit}>
            {error && <strong>{formatMessage(error)}</strong>}
            <div className={cx(s.stepRightCard, s.page6StepCard)}>
              <div className={s.landingMainContent}>

                {/* Top search bar */}
                <div className={s.page6TopSearch}>
                  <div className={s.page6TopSearchInner}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={s.page6TopSearchPinIcon}>
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <PlaceGeoSuggest
                      id={'page6-top'}
                      placeholder="Busca una dirección"
                      className={s.page6TopSearchInput}
                      containerClass={s.page6GeoContainer}
                      initialValue={searchAddress}
                      includedRegionCodes={BOLIVIA_INCLUDED_REGION_CODES}
                      regionCode={BOLIVIA_REGION_CODE}
                      locationBias={BOLIVIA_LOCATION_BIAS}
                      onSuggestSelect={this.handleSuggestSelect}
                      onChange={this.handlePlaceGeoSuggestChange}
                      onKeyDown={this.handleSearchKeyDown}
                    />
                  </div>
                </div>

                {/* Map */}
                <div className={s.page6MapContainer}>
                  {(!lat || !lng) && <Loader type={"text"} />}
                  {(lat != null && lng != null) &&
                    <Field
                      name="locationMap"
                      component={this.renderPlaceMap}
                      lat={lat}
                      lng={lng}
                      isMapTouched={isMapTouched}
                      mapWarning={formatMessage(messages.mapWarning)}
                      mapSuccess={formatMessage(messages.mapSuccess)}
                    />
                  }
                  {isFarFromAddress &&
                    <div className={s.page6MapWarning}>
                      <FormattedMessage id="pinFarFromAddress" defaultMessage="El marcador parece estar lejos de la dirección ingresada. ¿Quieres ajustarlo?" />
                    </div>
                  }
                </div>

                {/* Dirección label + input (manual, sin sugerencias ni búsqueda en mapa) */}
                <div className={s.page6AddressSection}>
                  <p className={s.page6AddressLabel}>Dirección</p>
                  <div className={s.page6AddressInputWrap}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={s.page6AddressPinIcon}>
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <input
                      id={'page6-address'}
                      type="text"
                      placeholder="Ejemplo: Av. Principal 123, La Paz, Bolivia"
                      className={s.page6AddressInput}
                      value={addressValue}
                      autoComplete="off"
                      onChange={this.handleAddressInputChange}
                    />
                  </div>
                </div>

                {/* OR divider */}
                <div className={s.page6OrDivider}>
                  <span className={s.page6OrText}>o</span>
                </div>

                {/* Use my location button */}
                <Button
                  className={s.page6UseLocationBtn}
                  onClick={this.handleUseCurrentLocation}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0084ff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                    <path d="M12 1v2M12 21v2M1 12h2M21 12h2" strokeOpacity="0.4" />
                  </svg>
                  Usar mi ubicación actual
                </Button>

              </div>

              <FooterButton
                isDisabled={error || isDisabled}
                nextPage={nextPage}
                previousPage={previousPage}
                previousPagePath={"bedrooms"}
                formPage={formPage}
                type={"submit"}
                step={step}
              />
            </div>
          </form>
        </div>
      </div>
    )
  }
}

Page6 = reduxForm({
  form: 'ListPlaceStep1', // a unique name for this form
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
  onSubmit: update
})(Page6);


// Decorate with connect to read form values
const selector = formValueSelector('ListPlaceStep1'); // <-- same as form name
Page6 = connect(
  state => {
    // can select values individually
    const locationMap = selector(state, 'locationMap');
    const isMapTouched = selector(state, 'isMapTouched');
    const lat = selector(state, 'lat');
    const lng = selector(state, 'lng');
    const street = selector(state, 'street');
    const city = selector(state, 'city');
    const stateName = selector(state, 'state');
    const country = selector(state, 'country');
    return {
      locationMap,
      isMapTouched,
      lat,
      lng,
      street,
      city,
      state: stateName,
      country,
      googleMapBrowserApiKey: state?.encodeConfigSettings?.googleMapBrowserApiKey,
    }
  },
  { getLocationData }
)(Page6);

export default injectIntl(withStyles(s, bt)(Page6));